import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
  Activity, Users, Coins, CalendarDays, TrendingUp, ShieldCheck, ArrowLeft, RefreshCw, ShieldAlert
} from "lucide-react";
import { toast } from "sonner";

const fmtMoney = (n) => "£" + (Number(n || 0)).toLocaleString();

function Kpi({ label, value, sub, icon: Icon, accent = "#CCFF00", testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-mono">{label}</div>
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: accent }}>
          <Icon strokeWidth={2.5} className="w-4 h-4 text-black" />
        </div>
      </div>
      <div className="h-display text-4xl tracking-tighter" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-[#A3A3A3] mt-1">{sub}</div>}
    </div>
  );
}

function TrendBars({ data, valueKey, accent }) {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div className="flex items-end gap-1.5 h-32">
      {data.map((d, i) => {
        const h = ((d[valueKey] || 0) / max) * 100;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5" title={`${d.week_starting}: ${d[valueKey]}`}>
            <div className="text-[10px] text-[#A3A3A3]">{d[valueKey] || 0}</div>
            <div className="w-full transition-all" style={{ height: `${h}%`, background: accent, minHeight: "2px" }} />
            <div className="text-[9px] text-[#525252] uppercase tracking-wider">{d.week_starting.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

function SplitBars({ data, accent }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return <div className="text-xs text-[#A3A3A3]">No data yet.</div>;
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="uppercase tracking-wider text-[#A3A3A3]">{k.replace("_", " ")}</span>
            <span className="text-white">{v} <span className="text-[#525252]">({Math.round((v / total) * 100)}%)</span></span>
          </div>
          <div className="h-1.5 bg-[#262626]">
            <div style={{ width: `${(v / total) * 100}%`, height: "100%", background: accent }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [statsRes, waiversRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/recent-waivers?limit=10").catch(() => ({ data: { items: [] } })),
      ]);
      setStats(statsRes.data);
      setWaivers(waiversRes.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load stats");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !stats) {
    return <div className="label-mono text-[#A3A3A3]">Loading stats…</div>;
  }
  if (!stats) return null;

  return (
    <div className="space-y-8" data-testid="admin-stats-page">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider mb-3" data-testid="stats-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to admin console
          </Link>
          <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>STATS · KPIs · TRENDS</div>
          <h1 className="h-display text-5xl tracking-tighter">Platform health.</h1>
          <p className="text-sm text-[#A3A3A3] mt-2">All metrics refresh on demand. Bookings and revenue update in real time as players check out.</p>
        </div>
        <button onClick={load} className="px-4 py-2 text-xs border border-[#262626] hover:border-[#CCFF00] uppercase tracking-wider inline-flex items-center gap-2" data-testid="stats-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 fade-up d1">
        <Kpi label="TOTAL USERS" value={stats.users.total}
             sub={`${stats.users.by_role?.coach || 0} coaches · ${stats.users.by_role?.student || 0} students · ${stats.users.by_role?.admin || 0} admin`}
             icon={Users} accent="#CCFF00" testid="kpi-users" />
        <Kpi label="THIS-MONTH BOOKINGS" value={stats.bookings.this_month}
             sub={`${stats.bookings.this_week} this week · ${stats.bookings.total} all-time`}
             icon={CalendarDays} accent="#FF4500" testid="kpi-bookings" />
        <Kpi label="REVENUE (PAID)" value={fmtMoney(stats.revenue.total)}
             sub={`${fmtMoney(stats.revenue.this_week)} this week · ${stats.bookings.paid} paid bookings`}
             icon={Coins} accent="#CCFF00" testid="kpi-revenue" />
        <Kpi label="PENDING APPLICATIONS" value={stats.applications.pending}
             sub={`Coach status: ${Object.entries(stats.users.coach_status || {}).map(([k, v]) => `${k}=${v}`).join(" · ") || "—"}`}
             icon={ShieldCheck} accent="#FF4500" testid="kpi-applications" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 fade-up d2">
        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">8-WEEK BOOKINGS TREND</div>
            <TrendingUp className="w-4 h-4 text-[#CCFF00]" />
          </div>
          <TrendBars data={stats.weekly_trend} valueKey="bookings" accent="#CCFF00" />
        </div>
        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">8-WEEK REVENUE TREND</div>
            <Coins className="w-4 h-4 text-[#FF4500]" />
          </div>
          <TrendBars data={stats.weekly_trend} valueKey="revenue" accent="#FF4500" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 fade-up d3">
        <div className="card-surface p-5">
          <div className="label-mono mb-4">BOOKINGS BY SPORT</div>
          <SplitBars data={stats.bookings.by_sport} accent="#CCFF00" />
        </div>
        <div className="card-surface p-5">
          <div className="label-mono mb-4">BOOKINGS BY SESSION TYPE</div>
          <SplitBars data={stats.bookings.by_type} accent="#FF4500" />
        </div>
      </div>

      <div className="card-surface p-5 fade-up d4">
        <div className="flex items-center justify-between mb-4">
          <div className="label-mono">TOP COACHES BY BOOKINGS</div>
          <Activity className="w-4 h-4 text-[#CCFF00]" />
        </div>
        {!stats.top_coaches.length ? (
          <div className="text-xs text-[#A3A3A3]">No bookings yet.</div>
        ) : (
          <div className="space-y-2">
            {stats.top_coaches.map((c, i) => (
              <Link key={c.coach_id} to={`/admin/users/${c.coach_id}`}
                    className="flex items-center justify-between hover:bg-[#141414] px-3 py-2 -mx-3 transition-colors group">
                <div className="flex items-center gap-3">
                  <div className="label-mono w-6 text-[#525252]">#{i + 1}</div>
                  <div className="text-white group-hover:text-[#CCFF00]">{c.name}</div>
                </div>
                <div className="h-display text-2xl tracking-tighter text-[#CCFF00]">{c.bookings}</div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="card-surface p-5 fade-up d5" data-testid="recent-waivers-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="label-mono inline-flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-[#FF4500]" /> RECENT ASSESSMENT OVERRIDES
          </div>
          <div className="text-[10px] text-[#525252] uppercase tracking-wider">Last {waivers.length || 0}</div>
        </div>
        {!waivers.length ? (
          <div className="text-xs text-[#A3A3A3]">No overrides yet — coaches are running assessments through normal bookings.</div>
        ) : (
          <div className="space-y-2">
            {waivers.map((w) => {
              const action = (w.action || "").replace("assessment_", "");
              const isWaiver = action === "waive";
              const isReset = action === "reset";
              const tagColor = isWaiver ? "#FF4500" : isReset ? "#525252" : "#CCFF00";
              const targetName = w.target?.name || w.target_id?.slice(0, 8) || "—";
              const targetEmail = w.target?.email || "";
              const actorName = w.actor?.name || w.actor?.email || w.actor_id?.slice(0, 8) || "—";
              const when = w.timestamp ? new Date(w.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
              return (
                <div key={w.id} className="grid sm:grid-cols-12 gap-3 px-3 py-2.5 -mx-3 hover:bg-[#141414] transition-colors items-center" data-testid={`waiver-row-${w.id}`}>
                  <div className="sm:col-span-2">
                    <span className="label-mono text-[10px] px-2 py-1 inline-block" style={{ background: tagColor, color: "#0A0A0A", fontWeight: 700 }}>
                      {action.toUpperCase()}
                    </span>
                  </div>
                  <div className="sm:col-span-4 min-w-0">
                    <Link to={`/players/${w.target_id}`} className="text-sm text-white hover:text-[#CCFF00] truncate inline-block max-w-full" data-testid={`waiver-player-link-${w.id}`}>{targetName}</Link>
                    {targetEmail && <div className="text-[10px] text-[#525252] truncate">{targetEmail}</div>}
                  </div>
                  <div className="sm:col-span-3 text-xs text-[#A3A3A3] min-w-0 truncate">by {actorName}</div>
                  <div className="sm:col-span-3 text-right text-[10px] text-[#525252] uppercase tracking-wider">{when}</div>
                  {w.reason && (
                    <div className="sm:col-span-12 text-xs text-[#A3A3A3] italic border-l-2 border-[#262626] pl-3 mt-1">
                      &ldquo;{w.reason}&rdquo;
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-xs text-[#525252] label-mono">GENERATED · {new Date(stats.generated_at).toLocaleString()}</div>
    </div>
  );
}
